import React, { useCallback, useEffect, useState } from 'react';
import useFetch from 'use-http';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { DateField, Field, SelectField } from './Master';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface ILeaseholdARReportsDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ILeaseholdArReportRow {
    id: string;
    clNo?: number;
    prNo?: number;
    total?: number;
    invDate?: string;
    invoiceNo?: string;
    tranType?: string;
    close?: string;
    tranNo?: string;
    dueDate?: string;
    lessor?: string;
    clientName?: string;
    projectName?: string;
    clientContact?: string;
    pastDue?: string;
    amountPaid?: number;
    remainingBalance?: number;
    tranAmt?: number;
    tranDate?: string;
    glDate?: string;
    entryNo?: string;
    entryDate?: string;
    paidNo?: string;
    refNo?: string;
    depNo?: string;
    checkNo?: string;
    checkDate?: string;
    checkAmt?: number;
    recDate?: string;
    depDate?: string;
    depositAmount?: number;
}

const PAGE_SIZE = 25;

const REPORT_OPTIONS = [
    { value: 'aging', label: 'Leasehold A/R Aging' },
    { value: 'ledger', label: 'Leasehold A/R Ledger' },
    { value: 'deposits', label: 'Leasehold Deposits' },
];

const formatDateToMMDDYYYY = (value: string): string => {
    if (!value) {
        return '';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
        return value;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatCurrency = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(parsed)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(parsed);
};

const getDefaultDateRange = (): { begin: string; end: string } => {
    const today = new Date();
    const begin = `${today.getFullYear()}-01-01`;
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const end = `${today.getFullYear()}-${month}-${day}`;

    return { begin, end };
};

const getColumnsForReport = (reportType: string): ITableColumn[] => {
    if (reportType === 'ledger') {
        return [
            { name: 'Close', mapping: 'close' },
            { name: 'Client', mapping: 'clNo' },
            { name: 'Tran Type', mapping: 'tranType' },
            { name: 'Tran No', mapping: 'tranNo' },
            { name: 'Tran Amt', mapping: 'tranAmt', format: formatCurrency },
            { name: 'Tran Date', mapping: 'tranDate', format: formatDateForDisplay },
            { name: 'GL Date', mapping: 'glDate', format: formatDateForDisplay },
            { name: 'Entry No', mapping: 'entryNo' },
            { name: 'Entry Date', mapping: 'entryDate', format: formatDateForDisplay },
            { name: 'Paid No', mapping: 'paidNo' },
            { name: 'Invoice No', mapping: 'refNo' },
            { name: 'Dep No', mapping: 'depNo' },
            { name: 'Check No', mapping: 'checkNo' },
            { name: 'Check Date', mapping: 'checkDate', format: formatDateForDisplay },
            { name: 'Amount Paid', mapping: 'amountPaid', format: formatCurrency },
            { name: 'Remaining Balance', mapping: 'remainingBalance', format: formatCurrency },
            { name: 'Client Name', mapping: 'clientName' },
            { name: 'Lessor', mapping: 'lessor' },
            { name: 'Due Date', mapping: 'dueDate', format: formatDateForDisplay },
        ];
    }

    if (reportType === 'deposits') {
        return [
            { name: 'Client', mapping: 'clNo' },
            { name: 'Check No', mapping: 'checkNo' },
            { name: 'Check Date', mapping: 'checkDate', format: formatDateForDisplay },
            { name: 'Check Amt', mapping: 'checkAmt', format: formatCurrency },
            { name: 'Rec Date', mapping: 'recDate', format: formatDateForDisplay },
            { name: 'Dep Date', mapping: 'depDate', format: formatDateForDisplay },
            { name: 'Paid No', mapping: 'paidNo' },
            { name: 'Dep No', mapping: 'depNo' },
            { name: 'Deposit Amount', mapping: 'depositAmount', format: formatCurrency },
            { name: 'Client Name', mapping: 'clientName' },
        ];
    }

    return [
        { name: 'Client', mapping: 'clNo' },
        { name: 'Pr. No.', mapping: 'prNo' },
        { name: 'Inv Date', mapping: 'invDate', format: formatDateForDisplay },
        { name: 'Invoice No', mapping: 'invoiceNo' },
        { name: 'Tran Type', mapping: 'tranType' },
        { name: 'Close', mapping: 'close' },
        { name: 'Due Date', mapping: 'dueDate', format: formatDateForDisplay },
        { name: 'Lessor', mapping: 'lessor' },
        { name: 'Client Name', mapping: 'clientName' },
        { name: 'Project Name', mapping: 'projectName' },
        { name: 'Client Contact', mapping: 'clientContact' },
        { name: 'Total', mapping: 'total', format: formatCurrency },
        { name: 'Amount Paid', mapping: 'amountPaid', format: formatCurrency },
        { name: 'Remaining Balance', mapping: 'remainingBalance', format: formatCurrency },
        { name: 'Past Due', mapping: 'pastDue' },
    ];
};

const LeaseholdARReportsDialog: React.FC<ILeaseholdARReportsDialogProps> = ({ isOpen, onClose }) => {
    const { get, response } = useFetch();

    const [reportType, setReportType] = useState('aging');
    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [beginDate, setBeginDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [rows, setRows] = useState<PagedResultSet<ILeaseholdArReportRow> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(false);
    const [isQuerying, setIsQuerying] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const reportDate = endDate || getDefaultDateRange().end;

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'id',
        columns: getColumnsForReport(reportType),
    };

    const loadRows = useCallback(async (): Promise<void> => {
        setIsQuerying(true);
        setRequestError(null);

        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());
            params.append('reportType', reportType);
            params.append('reportDate', formatDateToMMDDYYYY(reportDate));

            if (beginDate) {
                params.append('begDate', formatDateToMMDDYYYY(beginDate));
            }

            if (endDate) {
                params.append('endDate', formatDateToMMDDYYYY(endDate));
            }

            if (clientNo.trim()) {
                params.append('clNo', clientNo.trim());
            }

            if (projectNo.trim()) {
                params.append('prNo', projectNo.trim());
            }

            const data = await get(`/v1.0/leases/admin/leasehold-ar-reports?${params.toString()}`);

            if (!response.ok || !data) {
                setRequestError(
                    parseLeaseRecordsApiError(response.data || { title: 'Failed to query Leasehold A/R report records.' }, response.status)
                );
                setRows(undefined);
                return;
            }

            const items: ILeaseholdArReportRow[] = (data.items || []).map((row: ILeaseholdArReportRow, index: number) => ({
                ...row,
                id: `${reportType}-${row.clNo || ''}-${row.prNo || ''}-${row.invoiceNo || row.refNo || row.checkNo || ''}-${skip + index}`,
            }));

            const paged = new PagedResultSet<ILeaseholdArReportRow>();
            paged.items = items;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setRows(paged);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error querying Leasehold A/R report records.' }, 500));
        } finally {
            setIsQuerying(false);
        }
    }, [beginDate, clientNo, endDate, get, pageNumber, projectNo, reportDate, reportType, response]);

    useEffect((): void | (() => void) => {
        if (!isOpen) {
            return undefined;
        }

        if (!beginDate || !endDate) {
            const defaults = getDefaultDateRange();
            setBeginDate(defaults.begin);
            setEndDate(defaults.end);
        }

        setPageNumber(1);
        setShouldReload(true);
    }, [isOpen, beginDate, endDate]);

    useEffect((): void | (() => void) => {
        if (!isOpen) {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !shouldReload) {
            return;
        }

        void loadRows();
        setShouldReload(false);
    }, [isOpen, shouldReload, loadRows]);

    useEffect(() => {
        let timeoutId: number | undefined;

        if (isOpen) {
            timeoutId = window.setTimeout(() => {
                setPageNumber(1);
                setShouldReload(true);
            }, 350);
        }

        return () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [isOpen, reportType, clientNo, projectNo, beginDate, endDate]);

    const handleQuery = (): void => {
        setPageNumber(1);
        setShouldReload(true);
    };

    const handlePageChange = (newPage: number): void => {
        setPageNumber(newPage);
        setShouldReload(true);
    };

    const handleDownload = async (): Promise<void> => {
        setIsGenerating(true);
        setRequestError(null);

        try {
            const params = new URLSearchParams();
            params.append('reportType', reportType);
            params.append('reportDate', formatDateToMMDDYYYY(reportDate));
            params.append('begDate', formatDateToMMDDYYYY(beginDate));
            params.append('endDate', formatDateToMMDDYYYY(endDate));
            params.append('clNo', clientNo.trim());
            params.append('prNo', projectNo.trim());

            await get(`/v1.0/leases/admin/leasehold-ar-reports/report?${params.toString()}`);

            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to generate Leasehold A/R report.' }, response.status));
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const fileName = `LeaseholdAR_${reportType}_${clientNo.trim()}_${projectNo.trim()}.xlsx`;

            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error downloading Leasehold A/R report.' }, 500));
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={onClose}>
            <div style={dialogStyles.content} onClick={(e) => e.stopPropagation()}>
                <div style={dialogStyles.header}>
                    <h3 style={dialogStyles.title}>Leasehold A/R Reports</h3>
                    <button onClick={onClose} style={dialogStyles.closeButton} aria-label="Close Leasehold A/R report dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.fieldsGrid}>
                        <SelectField
                            label="Report"
                            value={reportType}
                            editable={true}
                            options={REPORT_OPTIONS}
                            onChange={(value) => setReportType(value || 'aging')}
                        />
                        <Field label="Client Number" value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                        <Field label="Project Number" value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                        <DateField label="Beginning Date" value={beginDate} editable={true} onChange={(value) => setBeginDate(value || '')} />
                        <DateField label="Ending Date" value={endDate} editable={true} onChange={(value) => setEndDate(value || '')} />
                    </div>

                    <div style={dialogStyles.actionRow}>
                        <button onClick={handleQuery} style={dialogStyles.secondaryButton} disabled={isQuerying || isGenerating}>
                            <div style={dialogStyles.buttonInner}>
                                {isQuerying && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                <span>{isQuerying ? 'Querying...' : 'Query'}</span>
                            </div>
                        </button>
                        <button onClick={handleDownload} style={dialogStyles.primaryButton} disabled={isGenerating || isQuerying}>
                            <div style={dialogStyles.buttonInner}>
                                {isGenerating && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                <span>{isGenerating ? 'Generating...' : 'Download Excel'}</span>
                            </div>
                        </button>
                    </div>

                    <div className="search-leases__table-wrapper leasehold-ar-report__table-wrapper" style={dialogStyles.tableSection}>
                        <Table isLoading={isQuerying} tableConfig={tableConfig} data={rows} enablePaging={true} onPageChange={handlePageChange} />
                    </div>
                </div>

                <div style={dialogStyles.footer}>
                    <button onClick={onClose} style={dialogStyles.secondaryButton} disabled={isGenerating || isQuerying}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 80,
        padding: '1rem',
    },
    content: {
        width: '95%',
        maxWidth: '1400px',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E5E7EB',
        padding: '1rem 1.25rem',
        backgroundColor: '#F9FAFB',
    },
    title: {
        margin: 0,
        color: '#111827',
        fontSize: '1rem',
        fontWeight: 600,
    },
    closeButton: {
        color: '#6B7280',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        overflow: 'hidden',
        minHeight: 0,
    },
    fieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.75rem',
    },
    hintText: {
        fontSize: '0.8rem',
        color: '#4B5563',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    tableSection: {
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '48vh',
        minHeight: '220px',
    },
    footer: {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        padding: '0.75rem 1.25rem',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    primaryButton: {
        border: 'none',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 600,
        cursor: 'pointer',
    },
    secondaryButton: {
        border: '1px solid #D1D5DB',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 500,
        cursor: 'pointer',
    },
    buttonInner: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
};

export default LeaseholdARReportsDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseholdARReportsDialog.tsx